/**
 * Recovers the real Unicode for math symbols that pdf.js drops to control
 * characters because their font has no ToUnicode table and uses Adobe's
 * "H#####" glyph-naming scheme (see mathPiGlyphMap.js for the why).
 *
 * pdf.js does NOT expose a font's /Encoding /Differences on the main thread,
 * so we read it ourselves from the raw PDF bytes. This is a deliberately
 * narrow, best-effort parser: it only understands classic (non-object-stream)
 * PDFs, which is exactly the population that ships these legacy Math-Pi fonts.
 * Anything it can't parse yields an empty map, and the sanitization dialog's
 * editable backstop still catches the dropped characters - so a parse miss
 * degrades to current behavior, never to a wrong conversion.
 *
 * The output is keyed by the font's BaseFont name (subset prefix included),
 * which matches the `name` pdf.js exposes on the resolved font object
 * (page.commonObjs.get(fontName).name), letting the caller correlate a
 * getTextContent item's font to its recovery table.
 */

import { resolveMathPiGlyph } from "@/utils/mathPiGlyphMap";

// Matches each indirect object's header + body up to the first `stream` or
// `endobj`. Font and Encoding dictionaries have no stream, so the dict body is
// always fully captured; stopping at `stream` keeps binary font programs out.
const OBJECT_RE = /(\d+)\s+\d+\s+obj\b([\s\S]*?)(?:stream|endobj)/g;

// Tokenizes a /Differences body: integers reset the code counter, /names are
// assigned to successive codes. Handles both "1/A/B" and "1 /A /B" spacing.
const DIFF_TOKEN_RE = /(\d+)|\/([^\s/[\]<>(){}]+)/g;

const BASEFONT_RE = /\/BaseFont\s*\/([^\s/[\]<>(){}]+)/;
const ENCODING_REF_RE = /\/Encoding\s+(\d+)\s+\d+\s+R/;
const DIFFERENCES_RE = /\/Differences\s*\[([\s\S]*?)\]/;

// ASCII bytes for "/Differences" - scanned directly so a PDF can be screened
// for custom encodings without allocating a full-file string for every file.
const DIFFERENCES_BYTES = Object.freeze([
	0x2f, 0x44, 0x69, 0x66, 0x66, 0x65, 0x72, 0x65, 0x6e, 0x63, 0x65, 0x73,
]); // "/Differences"

/**
 * True if the raw PDF contains a "/Differences" marker. For a Uint8Array this
 * is a single allocation-free byte scan; for the string form (tests, or a
 * caller that already has text) it is a plain indexOf. "/Differences" is pure
 * ASCII, so byte- and char-level matching are equivalent.
 */
function hasDifferencesMarker(pdfBytes) {
	if (typeof pdfBytes === "string") {
		return pdfBytes.indexOf("/Differences") !== -1;
	}
	const pat = DIFFERENCES_BYTES;
	const last = pdfBytes.length - pat.length;
	for (let i = 0; i <= last; i++) {
		if (pdfBytes[i] !== pat[0]) continue;
		let k = 1;
		while (k < pat.length && pdfBytes[i + k] === pat[k]) k++;
		if (k === pat.length) return true;
	}
	return false;
}

/** Decode raw PDF bytes as Latin-1; dict text is ASCII so this is lossless. */
function bytesToLatin1(bytes) {
	if (typeof bytes === "string") return bytes;
	let out = "";
	const CHUNK = 0x8000;
	for (let i = 0; i < bytes.length; i += CHUNK) {
		out += String.fromCharCode.apply(
			null,
			bytes.subarray(i, Math.min(i + CHUNK, bytes.length)),
		);
	}
	return out;
}

/** Parse a /Differences body into { charCode: glyphName }. */
function parseDifferences(body) {
	const map = {};
	let code = 0;
	let m;
	DIFF_TOKEN_RE.lastIndex = 0;
	while ((m = DIFF_TOKEN_RE.exec(body)) !== null) {
		if (m[1] !== undefined) {
			code = parseInt(m[1], 10);
		} else if (m[2] !== undefined) {
			map[code] = m[2];
			code += 1;
		}
	}
	return map;
}

/**
 * Build a glyph-recovery table from raw PDF bytes.
 * @param {Uint8Array | string} pdfBytes
 * @returns {Map<string, Map<number, string>>} BaseFont name -> (charCode ->
 *   recovered Unicode char). Empty when nothing is recoverable or on any error.
 */
export function buildGlyphRecoveryMap(pdfBytes) {
	const result = new Map();
	try {
		if (!pdfBytes || !pdfBytes.length) return result;

		// Cheap early-out: a PDF with no /Differences encoding has nothing to
		// recover. Screen for it straight off the raw bytes so the vast majority
		// of files - every well-behaved PDF - skip the full-file Latin-1 decode
		// entirely; only genuinely affected files pay for the decode and parse.
		if (!hasDifferencesMarker(pdfBytes)) return result;

		const text = bytesToLatin1(pdfBytes);

		// Pass 1: index every object body, and collect /Differences from any
		// object that has one (covers indirectly-referenced Encoding objects).
		const bodyByObj = new Map();
		const diffsByObj = new Map();
		let m;
		OBJECT_RE.lastIndex = 0;
		while ((m = OBJECT_RE.exec(text)) !== null) {
			const objNum = parseInt(m[1], 10);
			const body = m[2];
			bodyByObj.set(objNum, body);
			const d = DIFFERENCES_RE.exec(body);
			if (d) diffsByObj.set(objNum, parseDifferences(d[1]));
		}

		// Pass 2: for each font dict, find its Differences (inline or via ref)
		// and resolve recoverable glyphs to Unicode.
		for (const body of bodyByObj.values()) {
			const bf = BASEFONT_RE.exec(body);
			if (!bf) continue;
			const baseFont = bf[1];

			let diffs = null;
			const inline = DIFFERENCES_RE.exec(body);
			if (inline) {
				diffs = parseDifferences(inline[1]);
			} else {
				const ref = ENCODING_REF_RE.exec(body);
				if (ref) diffs = diffsByObj.get(parseInt(ref[1], 10)) || null;
			}
			if (!diffs) continue;

			for (const [codeStr, glyphName] of Object.entries(diffs)) {
				const ch = resolveMathPiGlyph(glyphName);
				if (ch == null) continue;
				const code = parseInt(codeStr, 10);
				if (!result.has(baseFont)) result.set(baseFont, new Map());
				result.get(baseFont).set(code, ch);
			}
		}
	} catch {
		return new Map();
	}
	return result;
}

/** Total number of (font, code) recovery entries in a map. */
export function recoveryEntryCount(recoveryMap) {
	let n = 0;
	for (const codeMap of recoveryMap.values()) n += codeMap.size;
	return n;
}

/**
 * Look up the per-code recovery table for a font, tolerating subset-prefix
 * mismatches (e.g. "ABCDEF+Foo" vs "Foo").
 * @param {Map<string, Map<number, string>>} recoveryMap
 * @param {string} baseFontName - typically commonObjs font `.name`.
 * @returns {Map<number, string> | null}
 */
export function codeMapForFont(recoveryMap, baseFontName) {
	if (!recoveryMap || !baseFontName) return null;
	if (recoveryMap.has(baseFontName)) return recoveryMap.get(baseFontName);
	const strip = (n) => n.replace(/^[A-Z]{6}\+/, "");
	const wanted = strip(baseFontName);
	for (const [name, codeMap] of recoveryMap) {
		if (strip(name) === wanted) return codeMap;
	}
	return null;
}

/**
 * Replace control-range characters in a text-content item string with their
 * recovered Unicode using a font's code map. Only remaps codes present in the
 * map; all other characters (including normal text) pass through untouched.
 * @param {string} str
 * @param {Map<number, string>} codeMap
 * @returns {string}
 */
export function recoverItemText(str, codeMap) {
	if (!str || !codeMap || codeMap.size === 0) return str;
	// Fast path: leave the (overwhelmingly common) unaffected string untouched
	// and allocate only when there is a control char we can actually recover.
	let needs = false;
	for (let i = 0; i < str.length; i++) {
		const code = str.charCodeAt(i);
		if (code < 0x20 && codeMap.has(code)) {
			needs = true;
			break;
		}
	}
	if (!needs) return str;
	let out = "";
	for (let i = 0; i < str.length; i++) {
		const code = str.charCodeAt(i);
		out += code < 0x20 && codeMap.has(code) ? codeMap.get(code) : str[i];
	}
	return out;
}
